import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Knowledge Base deletion confirmation modal with user notification preview
 */
const KnowledgeBaseDeletionModal = ({
  knowledgeBase,
  isOpen,
  onClose,
  onDelete
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  const [deleting, setDeleting] = useState(false);
  const [notificationPreview, setNotificationPreview] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [deleteReason, setDeleteReason] = useState('');
  const [associatedParrots, setAssociatedParrots] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);

  /**
   * Load associated parrots
   */
  const loadAssociatedParrots = async () => {
    try {
      setLoadingAssociations(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}/associations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAssociatedParrots(data.associations || []);
    } catch (error) {
      console.error('Error loading associations:', error);
      showToast('Failed to load parrot associations', 'error');
    } finally {
      setLoadingAssociations(false);
    }
  };

  /**
   * Generate notification preview
   */
  const generateNotificationPreview = () => {
    const ownerName = knowledgeBase.user_name || knowledgeBase.user_email || 'User';
    const kbName = knowledgeBase.name || 'Knowledge Base File';
    
    let preview = `Dear ${ownerName},\n\n`;
    preview += `We're writing to inform you that your knowledge base file "${kbName}" has been removed from your ParrotMate account.\n\n`;
    
    if (deleteReason) {
      preview += `Reason for removal: ${deleteReason}\n\n`;
    }
    
    if (associatedParrots.length > 0) {
      preview += `This file was associated with the following parrot${associatedParrots.length !== 1 ? 's' : ''}:\n`;
      associatedParrots.forEach(parrot => {
        preview += `• ${parrot.name || 'Unnamed Parrot'}\n`;
      });
      preview += `\nThese associations have been automatically removed.\n\n`;
    }
    
    preview += `If you believe this was done in error or have any questions, please contact our support team.\n\n`;
    preview += `Best regards,\nThe ParrotMate Team`;
    
    setNotificationPreview(preview);
  };

  /**
   * Handle deletion
   */
  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      showToast('Please provide a reason for deletion', 'warning');
      return;
    }

    try {
      setDeleting(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: deleteReason,
          send_notification: sendNotification,
          notification_content: sendNotification ? notificationPreview : null
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      showToast('Knowledge Base file deleted successfully', 'success');
      
      if (onDelete) {
        onDelete(knowledgeBase);
      }
      
      onClose();
    } catch (error) {
      console.error('Error deleting Knowledge Base:', error);
      showToast(error.message || 'Failed to delete Knowledge Base file', 'error');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (deleting) {
      return;
    }
    
    setDeleteReason('');
    setSendNotification(true);
    setNotificationPreview('');
    setAssociatedParrots([]);
    onClose();
  };

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && knowledgeBase?.id) {
      loadAssociatedParrots();
    }
  }, [isOpen, knowledgeBase?.id]);

  // Generate notification preview when dependencies change
  useEffect(() => {
    if (isOpen && knowledgeBase && !loadingAssociations) {
      generateNotificationPreview();
    }
  }, [isOpen, knowledgeBase, deleteReason, associatedParrots, loadingAssociations]);

  if (!isOpen || !knowledgeBase) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg border w-full max-w-2xl max-h-[90vh] overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex justify-between items-center ${
          theme === 'dark' 
            ? 'bg-red-900/20 border-red-500/30' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div>
            <h3 className={`text-lg font-medium flex items-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <span className="text-red-400 mr-2">⚠️</span>
              Delete Knowledge Base File
            </h3>
            <p className={`text-sm mt-1 ${
              theme === 'dark' ? 'text-red-300' : 'text-red-700'
            }`}>
              This action cannot be undone
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={deleting}
            className={`transition-colors disabled:opacity-50 ${
              theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          {/* File Information */}
          <div className={`mb-6 p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
            <h4 className={`font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>File to be deleted:</h4>
            <div className="space-y-1 text-sm">
              <div><span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Name:</span> <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{knowledgeBase.name}</span></div>
              <div><span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Owner:</span> <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{knowledgeBase.user_name || knowledgeBase.user_email}</span></div>
              <div><span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Size:</span> <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{knowledgeBase.file_size ? `${Math.round(knowledgeBase.file_size / 1024)} KB` : 'Unknown'}</span></div>
              <div><span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Created:</span> <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{knowledgeBase.created_at ? new Date(knowledgeBase.created_at).toLocaleDateString() : 'Unknown'}</span></div>
            </div>
          </div>

          {/* Associated Parrots Warning */}
          {loadingAssociations ? (
            <div className={`mb-6 p-4 border rounded-lg ${
              theme === 'dark' 
                ? 'bg-yellow-900/20 border-yellow-500/30' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400 mr-2"></div>
                <span className={theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}>Loading parrot associations...</span>
              </div>
            </div>
          ) : associatedParrots.length > 0 && (
            <div className={`mb-6 p-4 border rounded-lg ${
              theme === 'dark' 
                ? 'bg-yellow-900/20 border-yellow-500/30' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h4 className={`font-medium mb-2 flex items-center ${
                theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'
              }`}>
                <span className="mr-2">⚠️</span>
                Associated Parrots ({associatedParrots.length})
              </h4>
              <p className={`text-sm mb-3 ${
                theme === 'dark' ? 'text-yellow-200' : 'text-yellow-600'
              }`}>
                This Knowledge Base file is currently associated with the following parrot{associatedParrots.length !== 1 ? 's' : ''}. 
                These associations will be automatically removed:
              </p>
              <div className="space-y-2">
                {associatedParrots.map((parrot) => (
                  <div key={parrot.id} className={`flex items-center justify-between p-2 rounded ${
                    theme === 'dark' ? 'bg-yellow-900/10' : 'bg-yellow-100'
                  }`}>
                    <span className={theme === 'dark' ? 'text-yellow-100' : 'text-yellow-800'}>{parrot.name || 'Unnamed Parrot'}</span>
                    <span className={`text-xs ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>Owner: {parrot.user_name || parrot.user_email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deletion Reason */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Reason for Deletion <span className="text-red-400">*</span>
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Please provide a reason for deleting this Knowledge Base file..."
              disabled={deleting}
              required
            />
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              This reason will be included in the notification to the file owner
            </p>
          </div>

          {/* Notification Settings */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="sendNotification"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className={`mr-2 rounded text-red-600 focus:ring-red-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600'
                    : 'bg-white border-gray-300'
                }`}
                disabled={deleting}
              />
              <label htmlFor="sendNotification" className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Send notification email to file owner
              </label>
            </div>

            {sendNotification && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Notification Preview
                </label>
                <div className={`border rounded-md p-3 max-h-48 overflow-y-auto ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600' 
                    : 'bg-gray-50 border-gray-300'
                }`}>
                  <pre className={`text-sm whitespace-pre-wrap font-sans ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {notificationPreview}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Confirmation */}
          <div className={`p-4 border rounded-lg ${
            theme === 'dark' 
              ? 'bg-red-900/20 border-red-500/30' 
              : 'bg-red-50 border-red-200'
          }`}>
            <h4 className={`font-medium mb-2 ${
              theme === 'dark' ? 'text-red-300' : 'text-red-700'
            }`}>Confirm Deletion</h4>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-red-200' : 'text-red-600'
            }`}>
              By proceeding, you acknowledge that:
            </p>
            <ul className={`text-sm mt-2 space-y-1 list-disc list-inside ${
              theme === 'dark' ? 'text-red-200' : 'text-red-600'
            }`}>
              <li>This Knowledge Base file will be permanently deleted</li>
              <li>All parrot associations will be removed</li>
              <li>This action cannot be undone</li>
              {sendNotification && <li>The file owner will be notified via email</li>}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-between ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <button
            onClick={handleClose}
            disabled={deleting}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              theme === 'dark'
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || !deleteReason.trim()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 dark:text-white"
          >
            {deleting ? 'Deleting...' : 'Delete Knowledge Base File'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseDeletionModal;
